/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import type {Provider} from '@lexical/yjs';
import type {
  EditorState,
  LexicalCommand,
  LexicalEditor,
  NodeKey,
  RangeSelection,
} from 'lexical';
import type {Doc} from 'yjs';

import './index.css';

import {
  $createMarkNode,
  $getMarkIDs,
  $isMarkNode,
  $unwrapMarkNode,
  $wrapSelectionInMarkNode,
  MarkNode,
} from '../../nodes/lexical-mark-custom/index';
import {AutoFocusPlugin} from '@lexical/react/LexicalAutoFocusPlugin';
import {ClearEditorPlugin} from '@lexical/react/LexicalClearEditorPlugin';
import {useCollaborationContext} from '@lexical/react/LexicalCollaborationContext';
import {LexicalComposer} from '@lexical/react/LexicalComposer';
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {EditorRefPlugin} from '@lexical/react/LexicalEditorRefPlugin';
import LexicalErrorBoundary from '@lexical/react/LexicalErrorBoundary';
import {HistoryPlugin} from '@lexical/react/LexicalHistoryPlugin';
import {OnChangePlugin} from '@lexical/react/LexicalOnChangePlugin';
import {PlainTextPlugin} from '@lexical/react/LexicalPlainTextPlugin';
import {createDOMRange, createRectsFromDOMRange} from '@lexical/selection';
import {$isRootTextContentEmpty, $rootTextContent} from '@lexical/text';
import {mergeRegister, registerNestedElementResolver} from '@lexical/utils';
import {
  $getNodeByKey,
  $getSelection,
  $isRangeSelection,
  $isTextNode,
  CLEAR_EDITOR_COMMAND,
  COMMAND_PRIORITY_EDITOR,
  createCommand,
  KEY_ESCAPE_COMMAND,
} from 'lexical';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import * as React from 'react';
import {createPortal} from 'react-dom';

import {
  Comment,
  Comments,
  CommentStore,
  createComment,
  createThread,
  Thread,
  useCommentStore,
  wpAndTaskIdType,
} from '../../commenting';
import useModal from '../../hooks/useModal';
import CommentEditorTheme from '../../themes/CommentEditorTheme';
import Button from '../../ui/Button';
import ContentEditable from '../../ui/ContentEditable';
import Placeholder from '../../ui/Placeholder';
import addIcon from '../../../lexical/images/icons/addCommentIcon.svg';
import CheckIcon from "../../../lexical/images/icons/check-mark-line-icon.svg";
export const INSERT_INLINE_COMMAND: LexicalCommand<void> = createCommand(
  'INSERT_INLINE_COMMAND',
);
import closeIcon from "../../../lexical/images/icons/crossIcon.svg";
import NewMentionsPlugin from '../MentionsPlugin';
import { MentionNode } from '../../nodes/MentionNode';
import { $getPageRoot, LayoutContainerNode } from '../../nodes/LayoutContainerNode';
import { getSelectedPage } from '../../nodes/LayoutItemNode';

function AddCommentBox({
  anchorKey,
  editor,
  onAddComment,
  showComments
}: {
  anchorKey: NodeKey;
  editor: LexicalEditor;
  onAddComment: () => void;
  showComments: any;
}): JSX.Element {
  const boxRef = useRef<HTMLDivElement>(null);

  const updatePosition = useCallback(() => {
    editor.update(()=>{
    const boxElem = boxRef.current;
    const ispageRoot = $getPageRoot();
    const selectedPage = getSelectedPage();
    let pageRooteELem :HTMLElement | null = null;
    if(ispageRoot && selectedPage){
      const elem = editor.getElementByKey(selectedPage.getLatest().getKey())
      pageRooteELem = elem ? elem : null;
    }
    const rootElement = pageRooteELem !== null ? pageRooteELem : editor.getRootElement();
    const anchorElement = editor.getElementByKey(anchorKey);

    if (boxElem !== null && rootElement !== null && anchorElement !== null) {
      const {right} = rootElement.getBoundingClientRect();
      const {left} = rootElement.getBoundingClientRect();
      const {top} = anchorElement.getBoundingClientRect();
      boxElem.style.left = `${showComments ? left -20 : right - 20}px`;
      boxElem.style.top = `${top - 30}px`;
    }
    })
  }, [anchorKey, editor]);

  useEffect(() => {
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('resize', updatePosition);
    };
  }, [editor, updatePosition]);

  React.useLayoutEffect(() => {
    updatePosition();
  }, [anchorKey, editor, updatePosition]);

  return (
    <div className="CommentPlugin_AddCommentBox" ref={boxRef}>
        <button
         style={{display : "flex" , justifyContent : "center", alignItems : "center"}}
        className="CommentPlugin_AddCommentBox_button"
        onClick={onAddComment}
      >
      <img src={addIcon} alt="" />
      </button>
    </div>
  );
}

function EscapeHandlerPlugin({
  onEscape,
}: {
  onEscape: (e: KeyboardEvent) => boolean;
}): null {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return editor.registerCommand(
      KEY_ESCAPE_COMMAND,
      (event: KeyboardEvent) => {
        return onEscape(event);
      },
      2,
    );
  }, [editor, onEscape]);

  return null;
}

function PlainTextEditor({
  className,
  autoFocus,
  onEscape,
  onChange,
  editorRef,
  placeholder = '',
  mentionItems,
  setmentionedEmails
}: {
  autoFocus?: boolean;
  className?: string;
  editorRef?: {current: null | LexicalEditor};
  onChange: (editorState: EditorState, editor: LexicalEditor) => void;
  onEscape: (e: KeyboardEvent) => boolean;
  placeholder?: string;
  mentionItems: any;
  setmentionedEmails: any
}) {
  const initialConfig = {
    namespace: 'Commenting',
    nodes: [MentionNode],
    onError: (error: Error) => {
      throw error;
    },
    theme: CommentEditorTheme,
  };

  useEffect(() => {
    return () => {
      setmentionedEmails([])
    }
  }, [])
  return (
    <LexicalComposer initialConfig={initialConfig}>
      <div className="CommentPlugin_CommentInputBox_EditorContainer">
        <PlainTextPlugin
          contentEditable={<ContentEditable className={className} />}
          placeholder={<Placeholder>{placeholder}</Placeholder>}
          ErrorBoundary={LexicalErrorBoundary}
        />
        <OnChangePlugin onChange={onChange} />
        <HistoryPlugin />
        {autoFocus !== false && <AutoFocusPlugin />}
        <EscapeHandlerPlugin onEscape={onEscape} />
        <ClearEditorPlugin />
        {editorRef !== undefined && <EditorRefPlugin editorRef={editorRef} />}
      </div>
      <NewMentionsPlugin mentionItems={mentionItems} setmentionedEmails={setmentionedEmails}/>
    </LexicalComposer>
  );
}

function useOnChange(
  setContent: (html: string) => void,
  setCanSubmit: (canSubmit: boolean) => void,
) {
  return useCallback(
    (editorState: EditorState, _editor: LexicalEditor) => {
      editorState.read(() => {
        const root = _editor.getRootElement();
        const htmlContent = root?.innerHTML ?? "";
        setContent(htmlContent);
        setCanSubmit(htmlContent.trim().length > 0);
      });
    },
    [setCanSubmit, setContent],
  );
}


function CommentInputBox({
  editor,
  cancelAddComment,
  submitAddComment,
  userName,
  userEmail,
  mentionItems,
  mentionedEmails,
  setmentionedEmails,
  wpAndTaskId,
  setShowCommentInput
}: {
  cancelAddComment: () => void;
  editor: LexicalEditor;
  submitAddComment: (
    commentOrThread: Comment | Thread,
    isInlineComment: boolean,
    thread?: Thread,
    selection?: RangeSelection | null,
  ) => void;
  userName?: string | any,
  userEmail?: string;
  mentionItems: any;
  mentionedEmails: any;
  setmentionedEmails: any;
  wpAndTaskId: wpAndTaskIdType;
  setShowCommentInput: any
}) {
  const [content, setContent] = useState('');
  const [canSubmit, setCanSubmit] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const selectionState = useMemo(
    () => ({
      container: document.createElement('div'),
      elements: [],
    }),
    [],
  );
  const selectionRef = useRef<RangeSelection | null>(null);
  const author = useCollabAuthorName();

  const updateLocation = useCallback(() => {
    editor.getEditorState().read(() => {
      const selection = $getSelection();

      if ($isRangeSelection(selection)) {
        selectionRef.current = selection.clone();
        const anchor = selection.anchor;
        const focus = selection.focus;
        const range = createDOMRange(
          editor,
          anchor.getNode(),
          anchor.offset,
          focus.getNode(),
          focus.offset,
        );
        const boxElem = boxRef.current;
        if (range !== null && boxElem !== null) {
          const {left, bottom, width} = range.getBoundingClientRect();
          const selectionRects = createRectsFromDOMRange(editor, range);
          let correctedLeft =
            selectionRects.length === 1 ? left + width / 2 - 125 : left - 125;
          if (correctedLeft < 10) {
            correctedLeft = 10;
          }
          boxElem.style.left = `${correctedLeft}px`;
          boxElem.style.top = `${
            bottom +
            20 +
            (window.pageYOffset || document.documentElement.scrollTop)
          }px`;
          // console.log('setShowCommentInput 123123', boxElem.style.top)
          // if (parseFloat(boxElem.style.top) < 150) {
          //   console.log('can hide the box 123123')
          //   setShowCommentInput(false)
          // }
          const selectionRectsLength = selectionRects.length;
          const {container} = selectionState;
          const elements: Array<HTMLSpanElement> = selectionState.elements;
          const elementsLength = elements.length;

          for (let i = 0; i < selectionRectsLength; i++) {
            const selectionRect = selectionRects[i];
            let elem: HTMLSpanElement = elements[i];
            if (elem === undefined) {
              elem = document.createElement('span');
              elements[i] = elem;
              container.appendChild(elem);
            }
            const color = '255, 212, 0';
            const style = `position:absolute;top:${
              selectionRect.top +
              (window.pageYOffset || document.documentElement.scrollTop)
            }px;left:${selectionRect.left}px;height:${
              selectionRect.height
            }px;width:${
              selectionRect.width
            }px;background-color:rgba(${color}, 0.3);pointer-events:none;z-index:5;`;
            elem.style.cssText = style;
          }
          for (let i = elementsLength - 1; i >= selectionRectsLength; i--) {
            const elem = elements[i];
            container.removeChild(elem);
            elements.pop();
          }
        }
      }
    });
  }, [editor, selectionState]);

  React.useLayoutEffect(() => {
    updateLocation();
    const container = selectionState.container;
    const body = document.body;
    if (body !== null) {
      body.appendChild(container);
      return () => {
        body.removeChild(container);
      };
    }
  }, [selectionState.container, updateLocation]);

  useEffect(() => {
    window.addEventListener('resize', updateLocation);
    window.addEventListener('scroll', updateLocation, true); // capture scroll on any element
  
    return () => {
      window.removeEventListener('resize', updateLocation);
      window.removeEventListener('scroll', updateLocation, true);
    };
  }, [updateLocation]);

  const onEscape = (event: KeyboardEvent): boolean => {
    event.preventDefault();
    cancelAddComment();
    return true;
  };

  const submitComment = () => {
    console.log("this is called" , wpAndTaskId)
    if (canSubmit) {
      let quote = editor.getEditorState().read(() => {
        const selection = selectionRef.current;
        return selection ? selection.getTextContent() : '';
      });
      if (quote.length > 100) {
        quote = quote.slice(0, 99) + '…';
      }
      submitAddComment(
        createThread(
          quote, 
          [createComment(content, (userName ?? author), userEmail, mentionedEmails)], 
          undefined,  
          wpAndTaskId
        ),
        true,
        undefined,
        selectionRef.current,
      );
      selectionRef.current = null;
    }
  };

  const onChange = useOnChange(setContent, setCanSubmit);

  return (
    <div className="CommentPlugin_CommentInputBox" ref={boxRef}>
      <PlainTextEditor
        className="CommentPlugin_CommentInputBox_Editor"
        onEscape={onEscape}
        onChange={onChange}
        mentionItems={mentionItems}
        setmentionedEmails={setmentionedEmails}
      />
      <div className="CommentPlugin_CommentInputBox_Buttons">
        <Button
          onClick={cancelAddComment}
          className="CommentPlugin_CommentInputBox_Button">
          Cancel
        </Button>
        <Button
          onClick={submitComment}
          disabled={!canSubmit}
          className="CommentPlugin_CommentInputBox_Button primary">
          Comment
        </Button>
      </div>
    </div>
  );
}

function CommentsComposer({
  submitAddComment,
  thread,
  placeholder,
  userName,
  userEmail,
  mentionItems,
  mentionedEmails,
  setmentionedEmails

}: {
  placeholder?: string;
  submitAddComment: (
    commentOrThread: Comment,
    isInlineComment: boolean,
    // eslint-disable-next-line no-shadow
    thread?: Thread,
  ) => void;
  thread?: Thread;
  userName : string;
  userEmail: string;
  mentionItems:any;
  mentionedEmails: any;
  setmentionedEmails: any
}) {
  const [content, setContent] = useState('');
  const [canSubmit, setCanSubmit] = useState(false);
  const editorRef = useRef<LexicalEditor>(null);
  const author = useCollabAuthorName();

  const onChange = useOnChange(setContent, setCanSubmit);

  const submitComment = () => {
    if (canSubmit) {
      setmentionedEmails([])
      submitAddComment(createComment(content, (userName ?? author) , userEmail , mentionedEmails), false, thread);
      const editor = editorRef.current;
      if (editor !== null) {
        editor.dispatchCommand(CLEAR_EDITOR_COMMAND, undefined);
      }
    }
  };

  return (
    <>
      <PlainTextEditor
        className="CommentPlugin_CommentsPanel_Editor"
        autoFocus={false}
        onEscape={() => {
          return true;
        }}
        onChange={onChange}
        editorRef={editorRef}
        placeholder={placeholder}
        mentionItems={mentionItems}
        setmentionedEmails={setmentionedEmails}
      />
      <Button
        className="CommentPlugin_CommentsPanel_SendButton"
        onClick={submitComment}
        disabled={!canSubmit}>
        <i className="send" />
      </Button>
    </>
  );
}

function ShowDeleteCommentOrThreadDialog({
  commentOrThread,
  deleteCommentOrThread,
  onClose,
  thread = undefined,
}: {
  commentOrThread: Comment | Thread;

  deleteCommentOrThread: (
    comment: Comment | Thread,
    // eslint-disable-next-line no-shadow
    thread?: Thread,
  ) => void;
  onClose: () => void;
  thread?: Thread;
}): JSX.Element {
  return (
    <>
      Are you sure you want to delete this {commentOrThread.type}?
      <div className="Modal__content">
        <Button
          onClick={() => {
            deleteCommentOrThread(commentOrThread, thread);
            onClose();
          }}>
          Delete
        </Button>{' '}
        <Button
          onClick={() => {
            onClose();
          }}>
          Cancel
        </Button>
      </div>
    </>
  );
}

function CommentsPanelListComment({
  comment,
  deleteComment,
  thread,
  rtf,
  userName,
  userEmail,
  mentionedEmails,
  setmentionedEmails
}: {
  comment: Comment;
  deleteComment: (
    commentOrThread: Comment | Thread,
    // eslint-disable-next-line no-shadow
    thread?: Thread,
  ) => void;
  rtf: Intl.RelativeTimeFormat;
  thread?: Thread;
  userName?: string
  userEmail?: string
  mentionedEmails?: any
  setmentionedEmails?: any
}): JSX.Element {
  // const seconds = Math.round((Number(comment.timeStamp) - performance.now()) / 1000);
  // const minutes = Math.round(seconds / 60);
  const [modal, showModal] = useModal();

  return (
    <li className="CommentPlugin_CommentsPanel_List_Comment">
      <div className="CommentPlugin_CommentsPanel_List_Details">
        <span className="CommentPlugin_CommentsPanel_List_Comment_Author">
          {comment.author}
        </span>
        <span className="CommentPlugin_CommentsPanel_List_Comment_Time" style={{fontSize : "13px"}}>
          {/* · {seconds > -10 ? 'Just now' : rtf.format(minutes, 'minute')} */}
          · {comment.timeStamp}
        </span>
      </div>
<p
  className={
    comment.deleted ? "CommentPlugin_CommentsPanel_DeletedComment" : ""
  }
  style={{ whiteSpace: "pre-wrap" }}
  dangerouslySetInnerHTML={{ __html: comment.content }}
></p>


      {!comment.deleted && (
        <>
          {userName == comment.author && (
          <Button
            onClick={() => {
              showModal("Delete Comment", (onClose) => (
                <ShowDeleteCommentOrThreadDialog
                  commentOrThread={comment}
                  deleteCommentOrThread={deleteComment}
                  thread={thread}
                  onClose={onClose}
                />
              ));
            }}
            className="CommentPlugin_CommentsPanel_List_DeleteButton"
          >
            <i className="delete" />
          </Button>
        )}
          {modal}
        </>
      )}
    </li>
  );
}

function CommentsPanelList({
  activeIDs,
  comments,
  deleteCommentOrThread,
  listRef,
  submitAddComment,
  markNodeMap,
  userName,
  userEmail,
  resolveThread,
  mentionItems,
  mentionedEmails,
  setmentionedEmails,
  wpAndTaskId
}: {
  activeIDs: Array<string>;
  comments: Comments;
  deleteCommentOrThread: (
    commentOrThread: Comment | Thread,
    thread?: Thread,
  ) => void;
  listRef: {current: null | HTMLUListElement};
  markNodeMap: Map<string, Set<NodeKey>>;
  submitAddComment: (
    commentOrThread: Comment | Thread,
    isInlineComment: boolean,
    thread?: Thread,
  ) => void;
  userName: string
  userEmail: string;
  resolveThread: any;
  mentionItems: any,
  mentionedEmails: any;
  setmentionedEmails: any;
  wpAndTaskId: wpAndTaskIdType;
}): JSX.Element {
  const [editor] = useLexicalComposerContext();
  const [counter, setCounter] = useState(0);
  const [modal, showModal] = useModal();
  const rtf = useMemo(
    () =>
      new Intl.RelativeTimeFormat('en', {
        localeMatcher: 'best fit',
        numeric: 'auto',
        style: 'short',
      }),
    [],
  );

  useEffect(() => {
    // Used to keep the time stamp up to date
    const id = setTimeout(() => {
      setCounter(counter + 1);
    }, 10000);

    return () => {
      clearTimeout(id);
    };
  }, [counter]);

  console.log('comments', comments , wpAndTaskId)
  const filteredComments = comments.filter((commentOrThread : any) =>
    commentOrThread.wpAndTaskId &&
    commentOrThread.wpAndTaskId.workplanId === wpAndTaskId.workplanId &&
    commentOrThread.wpAndTaskId.taskId === wpAndTaskId.taskId
  );

  console.log('Filtered comments', filteredComments, wpAndTaskId);
  return (
    <ul className="CommentPlugin_CommentsPanel_List" ref={listRef}>
      {filteredComments.length > 0 ? filteredComments.map((commentOrThread) => {
        const id = commentOrThread.id;
        if (commentOrThread.type === 'thread') {
          const handleClickThread = () => {
            const markNodeKeys = markNodeMap.get(id);
            if (
              markNodeKeys !== undefined &&
              (activeIDs === null || activeIDs.indexOf(id) === -1)
            ) {
              const activeElement = document.activeElement;
              // Move selection to the start of the mark, so that we
              // update the UI with the selected thread.
              editor.update(
                () => {
                  const markNodeKey = Array.from(markNodeKeys)[0];
                  const markNode = $getNodeByKey<MarkNode>(markNodeKey);
                  if ($isMarkNode(markNode)) {
                    markNode.selectStart();
                  }
                },
                {
                  onUpdate() {
                    // Restore selection to the previous element
                    if (activeElement !== null) {
                      (activeElement as HTMLElement).focus();
                    }
                  },
                }
              );
            }
          };

          return (
            // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
            <li
              key={id}
              data-id={id} 
              onClick={handleClickThread}
              className={`CommentPlugin_CommentsPanel_List_Thread ${
                markNodeMap.has(id) ? "interactive" : ""
              } ${activeIDs.includes(id) ? "active":""}`}>
              <div className="CommentPlugin_CommentsPanel_List_Thread_QuoteBox">
                {/* <blockquote className="CommentPlugin_CommentsPanel_List_Thread_Quote">
                  {'> '}
                  <span>{commentOrThread.quote}</span>
                </blockquote> */}
                    <blockquote className="CommentPlugin_CommentsPanel_List_Thread_Quote">
                  {/* <img
                      className="add-toolbar-image-icon"
                      src={ChatIcon}
                      alt="Add Image"
                    /> */}
                  <span>{commentOrThread?.comments[0]?.author}</span>

                  <img
                    className={`add-toolbar-image-icon CommentPlugin_CommentsPanel_List_ResolveButton ${
                      commentOrThread?.resolved ? "opacity-full" : ""
                    }`}
                    src={CheckIcon}
                    alt="Resolve Comment"
                    onClick={() => resolveThread(commentOrThread)}
                  />
                </blockquote>
                {/* INTRODUCE DELETE THREAD HERE*/}
                {userName == commentOrThread?.comments[0]?.author &&
                <Button
                  onClick={() => {
                    showModal("Delete Thread", (onClose) => (
                      <ShowDeleteCommentOrThreadDialog
                        commentOrThread={commentOrThread}
                        deleteCommentOrThread={deleteCommentOrThread}
                        onClose={onClose}
                      />
                    ));
                  }}
                  className="CommentPlugin_CommentsPanel_List_DeleteButton"
                >
                  <i className="delete" />
                </Button>
                }
                {modal}
              </div>
              <ul className="CommentPlugin_CommentsPanel_List_Thread_Comments">
              {commentOrThread && commentOrThread.comments && commentOrThread.comments.length && commentOrThread.comments.map((comment) => {
                  if (comment.deleted) {
                    return null;
                  }
                  return (
                    <CommentsPanelListComment
                      key={comment.id}
                      comment={comment}
                      deleteComment={deleteCommentOrThread}
                      thread={commentOrThread}
                      rtf={rtf}
                      userName={userName}
                      userEmail ={userEmail}
                    />
                  );
                })}
              </ul>
              <div className="CommentPlugin_CommentsPanel_List_Thread_Editor">
              {!commentOrThread?.resolved && (
                <div className="CommentPlugin_CommentsPanel_List_Thread_Editor">
                  <CommentsComposer
                    submitAddComment={submitAddComment}
                    thread={commentOrThread}
                    // placeholder="Reply to comment..."
                    userName={userName}
                    userEmail={userEmail}
                    mentionItems={mentionItems}
                    mentionedEmails={mentionedEmails}
                    setmentionedEmails={setmentionedEmails}
                  />
                </div>
              )}
              </div>
            </li>
          );
        }
        return (
          <CommentsPanelListComment
            key={id}
            comment={commentOrThread}
            deleteComment={deleteCommentOrThread}
            rtf={rtf}
            userName={userName}
            userEmail ={userEmail}
          />
        );
      })
      :
      <div className="CommentPlugin_CommentsPanel_Empty">No Comments</div>
      }
    </ul>
  );
}

function CommentsPanel({
  activeIDs,
  deleteCommentOrThread,
  comments,
  submitAddComment,
  markNodeMap,
  userName,
  userEmail,
  resolveThread,
  setShowComments,
  mentionItems,
  mentionedEmails,
  setmentionedEmails,
  wpAndTaskId,
  setActiveIDs,
  commentId
}: {
  activeIDs: Array<string>;
  comments: Comments;
  deleteCommentOrThread: (
    commentOrThread: Comment | Thread,
    thread?: Thread,
  ) => void;
  markNodeMap: Map<string, Set<NodeKey>>;
  submitAddComment: (
    commentOrThread: Comment | Thread,
    isInlineComment: boolean,
    thread?: Thread,
  ) => void;
  userName : string,
  userEmail : string,
  resolveThread: any,
  setShowComments: any,
  mentionItems: any;
  mentionedEmails: any;
  setmentionedEmails: any;
  wpAndTaskId: wpAndTaskIdType;
  setActiveIDs: any;
  commentId?: string | any;
}): JSX.Element {
  const listRef = useRef<HTMLUListElement>(null);
  const isEmpty = comments.length === 0;
  const handleGoToComment = (id: string) => {
    if (!listRef.current) return;

    const target = listRef.current.querySelector<HTMLLIElement>(
      `li[data-id="${id}"]`
    );
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    setActiveIDs?.([id]); // <-- make sure you pass setActiveIDs from parent
  };
  useEffect(() => {
    const id = 'xpoct'
    if (!listRef.current) return;
      const target = listRef.current.querySelector<HTMLLIElement>(
      `li[data-id="${commentId}"]`
    );
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    setTimeout(() => {
      console.log('commentId', commentId)
      setActiveIDs?.([commentId]);
    },7000);
  },[]);

  return (
    <div className="CommentPlugin_CommentsPanel">
      <div className='CommentPlugin_CommentsPanel_HeaderDiv'>
      <h2 className="CommentPlugin_CommentsPanel_Heading">Comments</h2>
      <img
          onClick={() => setShowComments(false)}
          className="CommentPlugin_CommentsPanel_close_icon"
          width={12}
          height={12}
          src={closeIcon}
          alt=""
        />
      </div>
      {isEmpty ? (
        <div className="CommentPlugin_CommentsPanel_Empty">No Comments</div>
      ) : (
        <CommentsPanelList
          activeIDs={activeIDs}
          comments={comments}
          deleteCommentOrThread={deleteCommentOrThread}
          listRef={listRef}
          submitAddComment={submitAddComment}
          markNodeMap={markNodeMap}
          userName={userName}
          userEmail={userEmail}
          resolveThread={resolveThread}
          mentionItems={mentionItems}
          mentionedEmails={mentionedEmails}
          setmentionedEmails={setmentionedEmails}
          wpAndTaskId={wpAndTaskId}
        />
      )}
    </div>
  );
}

function useCollabAuthorName(): string {
  const collabContext = useCollaborationContext();
  const {yjsDocMap, name} = collabContext;
  return yjsDocMap.has('comments') ? name : 'Playground User';
}

export default function CommentPlugin({
  providerFactory,
  handleSaveComments,
  showComments,
  setShowComments,
  userName,
  userEmail,
  mentionItems,
  mentionedEmails,
  setmentionedEmails,
  wpAndTaskId,
  setShowCommentInput,
  showCommentInput,
  commentId
}: {
  providerFactory?: (id: string, yjsDocMap: Map<string, Doc>) => Provider;
  handleSaveComments : (comments: Comments) => void;
  setShowComments : any;
  showComments : boolean;
  userName: string;
  userEmail: string;
  mentionItems : any;
  mentionedEmails: any;
  setmentionedEmails: any
  wpAndTaskId: wpAndTaskIdType;
  showCommentInput: boolean;
  setShowCommentInput: any;
  commentId?: string | any;
}): JSX.Element {
  const collabContext = useCollaborationContext();
  const [editor] = useLexicalComposerContext();
  const commentStore = useMemo(() => {
    if (!(editor as any)?._commentStore) {
      (editor as any)._commentStore = new CommentStore(editor);
    }
    return (editor as any)?._commentStore;
  }, [editor]);
  const comments = useCommentStore(commentStore);
  const markNodeMap = useMemo<Map<string, Set<NodeKey>>>(() => {
    return new Map();
  }, []);
  const [activeAnchorKey, setActiveAnchorKey] = useState<NodeKey | null>();
  const [activeIDs, setActiveIDs] = useState<Array<string>>([]);
  // const [showComments, setShowComments] = useState(false);
  const {yjsDocMap} = collabContext;
  useEffect(() => {
    if (providerFactory) {
      const provider = providerFactory('comments', yjsDocMap);
      return commentStore.registerCollaboration(provider);
    }
  }, [commentStore, providerFactory, yjsDocMap]);

  const cancelAddComment = useCallback(() => {
    editor.update(() => {
      const selection = $getSelection();
      // Restore selection
      if (selection !== null) {
        selection.dirty = true;
      }
    });
    setShowCommentInput(false);
  }, [editor]);
  // useEffect(() => {
  //   if (comments && comments?.length > 0) {
  //     handleSaveComments(comments);
  //   }
  // }, [comments]);
  const deleteCommentOrThread = useCallback(
    (comment: Comment | Thread, thread?: Thread) => {
  
      if (comment.type === "comment") {
        if (userName === comment.author) {
          const deletionInfo = commentStore.deleteCommentOrThread(comment, thread);
          if (!deletionInfo) return;
          if (thread) {
            const allDeleted = thread.comments.every(
              (c) => c.id === comment.id || c.deleted === true
            );
  
            if (allDeleted) {
              commentStore.deleteCommentOrThread(thread);
  
              const markNodeKeys = markNodeMap.get(thread.id);
              if (markNodeKeys) {
                setTimeout(() => {
                  editor.update(() => {
                    for (const key of markNodeKeys) {
                      const node = $getNodeByKey(key);
                      if ($isMarkNode(node)) {
                        node.deleteID(thread.id);
                        if (node.getIDs().length === 0) {
                          $unwrapMarkNode(node);
                        }
                      }
                    }
                  });
                  handleSaveComments(commentStore.getComments());
                });
              } else {
                handleSaveComments(commentStore.getComments());
              }
  
              return;
            }
          }
  
          const { markedComment, index } = deletionInfo;
          commentStore.addComment(markedComment, thread, index);
          handleSaveComments(commentStore.getComments());
        }
      }
       else {
        if (comment.comments?.[0].author == userName) {
          commentStore.deleteCommentOrThread(comment);
        }
        // Remove ids from associated marks
        const id = thread !== undefined ? thread.id : comment.id;
        const markNodeKeys = markNodeMap.get(id);
        if (markNodeKeys !== undefined) {
          // Do async to avoid causing a React infinite loop
          setTimeout(() => {
            editor.update(() => {
              for (const key of markNodeKeys) {
                const node: null | MarkNode = $getNodeByKey(key);
                if ($isMarkNode(node)) {
                  node.deleteID(id);
                  if (node.getIDs().length === 0) {
                    $unwrapMarkNode(node);
                  }
                }
              }
            });
            handleSaveComments(commentStore.getComments());
          });
        } else {
          handleSaveComments(commentStore.getComments());
        }
      }
    },
    [commentStore, editor, markNodeMap, handleSaveComments, userName]
  );
  

  const submitAddComment = useCallback(
    (
      commentOrThread: Comment | Thread,
      isInlineComment: boolean,
      thread?: Thread,
      selection?: RangeSelection | null,
    ) => {
      console.log('submitAddComment is called')
      commentStore.addComment(commentOrThread, thread);
      handleSaveComments(commentStore.getComments());
      if (isInlineComment) {
        editor.update(() => {
          if ($isRangeSelection(selection)) {
            const isBackward = selection.isBackward();
            const id = commentOrThread.id;

            // Wrap content in a MarkNode
            $wrapSelectionInMarkNode(selection, isBackward, id);
          }
        });
        setShowCommentInput(false);
      }
    },
    [commentStore, editor],
  );
  const resolveThread = (thread?: Thread) => {
    if (thread && thread.type === "thread") {
      commentStore.resolveThread(thread);
    }
    handleSaveComments(commentStore.getComments())
  };
  useEffect(() => {
    const changedElems: Array<HTMLElement> = [];
    for (let i = 0; i < activeIDs.length; i++) {
      const id = activeIDs[i];
      const keys = markNodeMap.get(id);
      if (keys !== undefined) {
        for (const key of keys) {
          const elem = editor.getElementByKey(key);
          if (elem !== null) {
            elem.classList.add('selected');
            changedElems.push(elem);
            setShowComments(true);
          }
        }
      }
    }
    return () => {
      for (let i = 0; i < changedElems.length; i++) {
        const changedElem = changedElems[i];
        changedElem.classList.remove('selected');
      }
    };
  }, [activeIDs, editor, markNodeMap]);

  useEffect(() => {
    const markNodeKeysToIDs: Map<NodeKey, Array<string>> = new Map();

    return mergeRegister(
      registerNestedElementResolver<MarkNode>(
        editor,
        MarkNode,
        (from: MarkNode) => {
          return $createMarkNode(from.getIDs());
        },
        (from: MarkNode, to: MarkNode) => {
          // Merge the IDs
          const ids = from.getIDs();
          ids.forEach((id) => {
            to.addID(id);
          });
        },
      ),
      editor.registerMutationListener(MarkNode, (mutations) => {
        editor.getEditorState().read(() => {
          for (const [key, mutation] of mutations) {
            const node: null | MarkNode = $getNodeByKey(key);
            let ids: NodeKey[] = [];

            if (mutation === 'destroyed') {
              ids = markNodeKeysToIDs.get(key) || [];
            } else if ($isMarkNode(node)) {
              ids = node.getIDs();
            }

            for (let i = 0; i < ids.length; i++) {
              const id = ids[i];
              let markNodeKeys = markNodeMap.get(id);
              markNodeKeysToIDs.set(key, ids);

              if (mutation === 'destroyed') {
                if (markNodeKeys !== undefined) {
                  markNodeKeys.delete(key);
                  if (markNodeKeys.size === 0) {
                    markNodeMap.delete(id);
                  }
                }
              } else {
                if (markNodeKeys === undefined) {
                  markNodeKeys = new Set();
                  markNodeMap.set(id, markNodeKeys);
                }
                if (!markNodeKeys.has(key)) {
                  markNodeKeys.add(key);
                }
              }
            }
          }
        });
      }),
      editor.registerUpdateListener(({editorState, tags}) => {
        editorState.read(() => {
          const selection = $getSelection();
          let hasActiveIds = false;
          let hasAnchorKey = false;

          if ($isRangeSelection(selection)) {
            const anchorNode = selection.anchor.getNode();

            if ($isTextNode(anchorNode)) {
              const commentIDs = $getMarkIDs(
                anchorNode,
                selection.anchor.offset,
              );
              if (commentIDs !== null) {
                setActiveIDs(commentIDs);
                hasActiveIds = true;
              }
              if (!selection.isCollapsed()) {
                setActiveAnchorKey(anchorNode.getKey());
                hasAnchorKey = true;
              }
            }
          }
          if (!hasActiveIds) {
            setActiveIDs((_activeIds) =>
              _activeIds.length === 0 ? _activeIds : [],
            );
          }
          if (!hasAnchorKey) {
            setActiveAnchorKey(null);
          }
          if (!tags.has('collaboration') && $isRangeSelection(selection)) {
            setShowCommentInput(false);
          }
        });
      }),
      editor.registerCommand(
        INSERT_INLINE_COMMAND,
        () => {
          const domSelection = window.getSelection();
          if (domSelection !== null) {
            domSelection.removeAllRanges();
          }
          setShowCommentInput(true);
          return true;
        },
        COMMAND_PRIORITY_EDITOR,
      ),
    );
  }, [editor, markNodeMap]);

  const onAddComment = () => {
    editor.dispatchCommand(INSERT_INLINE_COMMAND, undefined);
  };

  return (
    <>
      {showCommentInput &&
        createPortal(
          <CommentInputBox
            editor={editor}
            cancelAddComment={cancelAddComment}
            submitAddComment={submitAddComment}
            userName={userName}
            userEmail={userEmail}
            mentionItems={mentionItems}
            mentionedEmails={mentionedEmails}
            setmentionedEmails={setmentionedEmails}
            wpAndTaskId={wpAndTaskId}
            setShowCommentInput={setShowCommentInput}
          />,
          document.body,
        )}
      {activeAnchorKey !== null &&
        activeAnchorKey !== undefined &&
        !showCommentInput &&
        createPortal(
          <AddCommentBox
            anchorKey={activeAnchorKey}
            editor={editor}
            onAddComment={onAddComment}
            showComments={showComments}
          />,
          document.body,
        )}
      {/* {createPortal(
        <Button
          className={`CommentPlugin_ShowCommentsButton ${
            showComments ? 'active' : ''
          }`}
          onClick={() => setShowComments(!showComments)}
          title={showComments ? 'Hide Comments' : 'Show Comments'}>
          <i className="comments" />
        </Button>,
        document.body,
      )} */}
      {showComments &&
        createPortal(
          <CommentsPanel
            comments={comments}
            submitAddComment={submitAddComment}
            deleteCommentOrThread={deleteCommentOrThread}
            activeIDs={activeIDs}
            markNodeMap={markNodeMap}
            userName={userName}
            userEmail={userEmail}
            resolveThread={resolveThread}
            setShowComments={setShowComments}
            mentionItems={mentionItems}
            mentionedEmails={mentionedEmails}
            setmentionedEmails={setmentionedEmails}
            wpAndTaskId={wpAndTaskId}
            setActiveIDs={setActiveIDs}
            commentId={commentId}
          />,
          document.body,
        )}
    </>
  );
}
